/** The form builder, round one (brief docs/briefs/forms-round-one.md, "Also in round one: every form has an address of its own"; red team
 *  report findings 4 and 23): the names a form and its fields go by. Pure, shared by the API (what it accepts) and the builder (what it
 *  proposes), so the builder never proposes a name the server refuses.
 *  - a field's **key** is its short internal name (`order_number`): round two's embed and the values passed in a page address use it as a
 *    name, so lower-case letters, digits and underscores, starting with a letter;
 *  - a form's **slug** is its short address name (`contact-us`): the projects' shape without their mailbox list (question 13: "contact",
 *    "support" and "help" are the most natural form addresses, and a form is no mailbox). */

export const FORM_KEY_MAX = 40;
export const FORM_SLUG_MAX = 40;
/** a form's name, at most 80 characters (red team finding 6) */
export const FORM_NAME_MAX = 80;

const KEY = /^[a-z][a-z0-9_]*$/;
/** lower-case letters, digits and underscores, starting with a letter, 1 to 40 characters */
export const isFormFieldKey = (key: string): boolean => key.length > 0 && key.length <= FORM_KEY_MAX && KEY.test(key);

/** plain lower-case letters and digits of a text, every other run replaced by `sep` ("Ärger & Fragen!" → "arger-fragen") */
const plainWords = (text: string, sep: string): string => text.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, sep).split(sep).filter(Boolean).join(sep);

/** `base`, or `base<sep>2`, `base<sep>3`, … the first not in `taken`, cut so the whole stays within `max` */
function uniqueName(base: string, taken: readonly string[], sep: string, max: number): string {
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let n = 2; ; n++) {
    const suffix = `${sep}${n}`;
    const head = base.slice(0, max - suffix.length).replace(new RegExp(`\\${sep}+$`), "");
    const name = `${head}${suffix}`;
    if (!used.has(name)) return name;
  }
}

/** The key the builder proposes for a label, unique among `taken`: "Order number" → `order_number` (then `_2`, `_3`); a label without a
 *  letter or digit gives `field`; one that starts with a digit gets `field_` in front. */
export function suggestFieldKey(label: string, taken: readonly string[]): string {
  const words = plainWords(label, "_");
  const started = !words ? "field" : /^[a-z]/.test(words) ? words : `field_${words}`;
  const base = started.slice(0, FORM_KEY_MAX).replace(/_+$/, "");
  return uniqueName(base, taken, "_", FORM_KEY_MAX);
}

export type FormSlugProblem = "empty" | "shape";
/** null, or what is wrong with a form's slug: lower-case letters, digits and single hyphens inside, 1 to 40 characters */
export function checkFormSlug(slug: string): FormSlugProblem | null {
  if (!slug) return "empty";
  return slug.length <= FORM_SLUG_MAX && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ? null : "shape";
}

/** The slug a new form gets from its name, unique among `taken`: "Contact us" → `contact-us` (then `-2`, `-3`); a name without a letter or
 *  digit gives `form`. */
export function formSlugify(name: string, taken: readonly string[]): string {
  const base = plainWords(name, "-").slice(0, FORM_SLUG_MAX).replace(/-+$/, "") || "form";
  return uniqueName(base, taken, "-", FORM_SLUG_MAX);
}

/** A form's own address: `https://<workspace>.omnabase.com/forms/<form>`, the help centre's host, no project in the path (round two serves
 *  the page) */
export const formPageUrl = (workspaceSlug: string, formSlug: string): string => `https://${workspaceSlug}.omnabase.com/forms/${formSlug}`;

/** A duplicate's name (red team finding 9, question 11): "<name> (copy)", then "<name> (copy 2)", … the first not taken (ignoring case),
 *  the name cut so the whole stays within 80 characters. */
export function formCopyName(name: string, taken: readonly string[]): string {
  const used = new Set(taken.map((t) => t.toLowerCase()));
  for (let n = 1; ; n++) {
    const suffix = n === 1 ? " (copy)" : ` (copy ${n})`;
    const candidate = `${name.slice(0, FORM_NAME_MAX - suffix.length).trimEnd()}${suffix}`;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
}
